import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {connect} from 'react-redux';
import moment from 'moment';


import {parseResponse, PortfolioHandlerResource, VirtualPortfolioHandlerResource} from "../../utils/api";
import {
  getFromStorage,
  SHARED_SETTINGS_KEY,
} from '../../utils/storage';
import {
  isCumulativeDisplayEnabled,
  saveCustomerId
} from "../../containers/CustomerDashboard/utils";
import {
  BEGINNING,
  CUSTOM,
  DEFAULT_PERFORMANCE_TIME_TYPE,
  LONGER_PORTFOLIO_COUNTS,
  LONGER_TIME_FRAME_YEARS,
  PERFORMANCE_TIME_TYPE,
  REPORT_DATE_PERFORMANCE_TIME_TYPE_MAPPING
} from '../../utils/constants';
import {
  AGGREGATED_PORTFOLIO_ID, getErrorMessage,
  getPortfolioMinStartOfInvestment, getSubSystemConfigItem,
  PortfolioUtils,
} from "../../utils/utils";
import {brokerLogout, customerLogout} from '../../containers/Authentication/Auth/utils';
import GoogleAnalytics from "../../utils/GoogleAnalytics";
import {setSharedSettings} from '../SharedSettingsProvider/actions';
import {GROUP_ACTION_DEPOT} from "../Charts/InstrumentsAllocationTable/constants";
import {isProfessionalViewAssetsEnabled} from "../TradingStore/utils";
import {defaultDataBody, showErrors} from "../../utils/dataProviderUtils";
import {
  convertSettingsToSelectedPortfolios,
  filterOutPortfoliosToExclude
} from "../../containers/DashboardSettings/components/DasboardSettingWindowV2";
import {
  getCustomerDashboardSelector,
  getInvestmentPlatformSelector,
  getSharedSettingsSelector
} from "../../utils/redaxSelectors";
import {
  REPORT_TYPES
} from "../../containers/DashboardSettings/components/CustomersList/components/ReportType/constants";
import {getPortfolioColor} from "../../containers/CustomerDashboardV2/utils";
import {isCancel, useCancelableRequest} from "../../hooks/useCancelableRequest";

// TODO move to one state together with dates and depots
const defaultChartsSettings = {
  historical: {
    withInvestiertesAmount: false,
    aggregateSelected: false,
    withEmptyAssets: true
  },
  performance: {
    withBenchmark: false,
    currently_selected_benchmarks: [],
    aggregateSelected: false
  },
  rollingVolatilityChart: {
    aggregateSelected: true
  },
  rollingSharpeRatioChart: {
    aggregateSelected: true
  },
  global: {
    withHistoricalPortfolios: undefined,
    withProfitLoss: undefined,
    withPaymentPlans: undefined,
    withTransactionSaldo: undefined
  }
};


const mapStateToProps = (state) => ({
  customerDashboard: getCustomerDashboardSelector(state),
  sharedSettings: getSharedSettingsSelector(state),
  investmentPlatform: getInvestmentPlatformSelector(state)
});

export const getSelectedAssetsIds = (assets) => _.flatten(assets.filter((asset) => asset.ids).map((asset) => asset.ids))

export const getUpdatedTwrData = (oldTwrData, data, portfolioToUpdate, additionalPortfoliosToUpdate) => {
  let portfolioToUpdateIds = [portfolioToUpdate.id];
  additionalPortfoliosToUpdate.forEach(pf => portfolioToUpdateIds.push(pf.id));

  const updatedPortfolios = oldTwrData.data.portfolios.map(portfolio => {
    // in case customer has only 1 portfolio set benchmark data into Aggregated
    if (portfolioToUpdate && [...portfolioToUpdateIds, AGGREGATED_PORTFOLIO_ID].includes(portfolio.id)) {
      let updatedPortfolio = data.find(item => portfolioToUpdateIds.includes(item.id));
      let updatedPortfolioBenchmarkName = updatedPortfolio && updatedPortfolio.benchmark_name;
      updatedPortfolio = updatedPortfolio && updatedPortfolio.time_series && updatedPortfolio.time_series.portfolio || [];

      try {
        const benchmark_first_date = updatedPortfolio[0]['date'];
        portfolio.benchmark_performance_detailed.tracking_start_date = benchmark_first_date;
        const time_series_first_date = portfolio.timeseries.portfolio[0]['date'];
        if (benchmark_first_date > time_series_first_date) {
          const zero_item = {
            date: time_series_first_date,
            value: 0.0,
            cum_ret_g: 0
          };
          updatedPortfolio = [zero_item, ...updatedPortfolio]
        }
      } catch (e) {

      }

      portfolio.benchmark_performance_detailed.portfolio = updatedPortfolio;

      if (updatedPortfolioBenchmarkName) {
        portfolio.benchmark_name = updatedPortfolioBenchmarkName
      }

    }
    return {...portfolio};
  });

  return {
    ...oldTwrData,
    data: {...oldTwrData.data, portfolios: updatedPortfolios},
    timestamp: new Date().getTime()
  };
}

const getPortfolioDataByKey = (key, portfolioData, portfolios, groupedPortfolios) => {
  const result = {...portfolioData};
  if (!portfolioData.data || !portfolioData.data.hasOwnProperty(key)) return result;

  let keyData = _.get(portfolioData, `data.${key}.data`);
  const keyErrors = _.get(portfolioData, `data.${key}.errors`);

  if(_.isArray(keyData)){
    keyData = keyData.map((p) => {
      if(p.is_group){
        // in case it's group of assets set color for assets/components
        return {
          ...p,
          components: p.components.map((c) => ({
            ...c,
            color: (c.depots || []).map(d => _.get(groupedPortfolios, [d, 'color']))
          }))
        }
      } else {
        return {
          ...p,
          color: getPortfolioColor(portfolios.data, p)
        }
      }
    })
  }

  return {
    ...result,
    data: keyData,
    errors: portfolioData.errors || keyErrors
  }

};

const toggleLoadingStatus = (callbacks) => {
  callbacks.forEach(callback => callback({
    data: undefined,
    errors: true,
    loading: false
  }));
};

/**
 * HOC for retrieving dashboard data
 */
const withDashboardData = (WrappedComponent, useAssetsApproach=false) => {
  return connect(mapStateToProps)((props) => {
    const {
      computedMatch: {
        params
      },
      auth: {
        user
      },
      isCurrentCustomerData,
      isVirtual,
    } = props;

    const dataProvider = isVirtual ? VirtualPortfolioHandlerResource : PortfolioHandlerResource;

    const [isMounted, setMountingStatus] = useState(false);

    const [customer, setCustomer] = useState({...defaultDataBody});
    const [portfolios, setPortfolios] = useState({...defaultDataBody});
    const [investmentData, setInvestmentData] = useState({...defaultDataBody});
    const [portfolioData, setPortfolioData] = useState({...defaultDataBody, updatedAt: undefined});
    const getCancelToken = useCancelableRequest();
    const [dataLoading, setDataLoading] = useState(false);
    const [instrumentsGroupBy, setInstrumentsGroupBy] = useState(GROUP_ACTION_DEPOT);

    const [chartsSettings, setChartsSettings] = useState({...defaultChartsSettings});
    const [reportType, setReportType] = useState(REPORT_TYPES.BASIC.value);

    const handleChartSettingsChange = (chart, field, data) => {
      setChartsSettings(prevChartSettings => {
        const updatedChartsSettings = {...prevChartSettings};
        if(!updatedChartsSettings[chart]) updatedChartsSettings[chart] = {};

        updatedChartsSettings[chart][field] = data;
        return updatedChartsSettings;
      })
    };

    const initCustomerData = (customerData) => {
      // set default chart settings with values from dashboard settings

      // report_type
      let report_type = _.get(customerData, 'dashboard_settings.report_type', REPORT_TYPES.BASIC.value);
      setReportType(report_type);

      // historical
      let withInvestiertesAmount = _.get(customerData, 'dashboard_settings.with_invested_capital', false);
      let aggregateSelectedHistorical = _.get(customerData, 'dashboard_settings.aggregate_historical_chart_portfolios', false);
      // performance
      let withBenchmark = _.get(customerData, 'dashboard_settings.benchmark_enabled', false);
      let aggregateSelectedPerformance = _.get(customerData, 'dashboard_settings.aggregate_performance_chart_portfolios', false);
      let preSelectedBenchmarks = _.get(customerData, 'dashboard_settings.benchmarks', []);
      if (!_.isEmpty(preSelectedBenchmarks)) {
        preSelectedBenchmarks = preSelectedBenchmarks.map(benchmark_data => {
          let percentage = parseInt(benchmark_data.percentage);
          return {
            ...benchmark_data,
            benchmark: benchmark_data.id,
            percentage: percentage,
            weight: percentage / 100,
          }
        })
      }
      // global firstLoad
      let withPaymentPlans = _.get(customerData, 'dashboard_settings.with_payment_plans', true);
      let withProfitLoss = _.get(customerData, 'dashboard_settings.with_profit_loss', true);
      let withHistoricalPortfolios = _.get(customerData, 'dashboard_settings.include_historical_portfolios', true);
      let withTransactionSaldo = _.get(customerData, 'dashboard_settings.with_transaction_saldo', true);
      let aggregateSelected = true;
      // if only one portfolio is selected, and it is not aggregated -> aggregateSelected should be turned off
      // selectedPortfolios is undefined on init render => either aggregated or single is selected, so there is no need check if cumulative display enabled
      if(selectedPortfolios && !isCumulativeDisplayEnabled(selectedPortfolios)){
        aggregateSelectedHistorical = false;
        aggregateSelectedPerformance = false;
        aggregateSelected = false;
      }

      let updatedChartSettings = {
        historical: {
          withInvestiertesAmount: withInvestiertesAmount,
          aggregateSelected: aggregateSelectedHistorical,
          withEmptyAssets: true
        },
        performance: {
          withBenchmark: withBenchmark,
          aggregateSelected: aggregateSelectedPerformance,
          currently_selected_benchmarks: preSelectedBenchmarks
        },
        rollingVolatilityChart: {
          aggregateSelected: aggregateSelected
        },
        rollingSharpeRatioChart: {
          aggregateSelected: aggregateSelected
        },
        global: {
          withHistoricalPortfolios: withHistoricalPortfolios,
          withProfitLoss: withProfitLoss,
          withPaymentPlans: withPaymentPlans,
          withTransactionSaldo: withTransactionSaldo
        }
      };
      setChartsSettings(updatedChartSettings);

      // fetch data and track load time to google analytics on first render
      fetchData(updatedChartSettings, customerData)  // pass data in case state was not quick enough to update
        .then(() => {
          setDataLoading(false);
          setMountingStatus(true);
          const end_time = new Date().getTime();
          const value = end_time - startLoadTime.current;
          GoogleAnalytics.trackDashboardPageLoadTime(value, user.is_broker, customerData.customer_id)
        });
    };

    // data fetching options with default values for first data fetching
    const [selectedPeriodData, setSelectedPeriodData] = useState({
      selectedDates: DEFAULT_PERFORMANCE_TIME_TYPE.getDateRange(),
      selectedDatesType: DEFAULT_PERFORMANCE_TIME_TYPE
    });
    const {selectedDates, selectedDatesType} = selectedPeriodData;

    const [selectedPortfolios, setSelectedPortfolios] = React.useState(undefined);

    const [assets, setAssets] = React.useState([]);
    const [assetsLoading, setAssetsLoading] = React.useState(false);
    const [inactiveAssets, setInactiveAssets] = React.useState([]);
    const [selectedAssets, setSelectedAssets] = React.useState([]);

    let appSettings = {};
    if (getFromStorage(SHARED_SETTINGS_KEY)) {
      appSettings = getFromStorage(SHARED_SETTINGS_KEY).app_settings || {}
    }

    const calculateSelectedRange = (customerPortfolios=[]) => {
      let date_limit = appSettings.date_limit || LONGER_TIME_FRAME_YEARS;
      let date_scale = appSettings.date_scale || 'years';
      let portfolios_limit = appSettings.portfolios_limit || LONGER_PORTFOLIO_COUNTS;
      let isBig = false;
      var id = moment().unix();
      if (selectedDates.end && selectedDates.start) {
        isBig = selectedDates.end.diff(selectedDates.start, date_scale) > date_limit
      } else if (selectedDatesType === DEFAULT_PERFORMANCE_TIME_TYPE) {
        isBig = true
      }
      if (selectedPortfolios) {
        if (selectedPortfolios.length > portfolios_limit)
          isBig = true
      } else if (customerPortfolios.length > portfolios_limit) {
        isBig = true
      }
      return {id, isBig}
    };

    const [selectedRange, setSelectedRange] = useState(calculateSelectedRange);
    const dataFetchingOptions = React.useRef({
      depot_id: undefined,
      excluded_depot_id: undefined,
      start_date : undefined,
      end_date : undefined
    });

    const startLoadTime = React.useRef(null);

    const getRequestedCustomerId = () => {
      if (isCurrentCustomerData) {
        return user && user.customer_id;
      }
      return params.customer_id;
    };

    //#region Life hooks

    /**
     * Call, when component did mount
     */
    useEffect(() => {
      const customer_id = getRequestedCustomerId();
      if(customer_id) saveCustomerId(customer_id);
    }, []);

    /*
     * Call, when data fetching options were updated, to download new data
     */

    useEffect(() => {
      fetchCustomer();
    }, [props.computedMatch.params.customer_id]);

    // useEffect(() => {
    //
    //   if (isProfessionalViewAssetsEnabled()) {
    //
    //     setAssetsLoading(true);
    //
    //     const startDate = selectedDates.start && selectedDates.start.format('YYYY-MM-DD') || undefined;
    //     const endDate = selectedDates.end && selectedDates.end.format('YYYY-MM-DD') || undefined;
    //
    //     const isAggregatedSelected = !assets.length || assets.length === selectedAssets.length;
    //
    //     dataProvider.getBasicPortfolioData(getRequestedCustomerId(), false, startDate, endDate, true)
    //       .then((response) => {
    //         parseResponse(response, 'portfolio_data', (data) => {
    //
    //           let activeComponents = data.components;
    //           const inactiveComponents = data.sold_before_period_components || [];
    //
    //           activeComponents = activeComponents.reduce((result, component) => {
    //             const isin = _.get(component, 'financial_information.asset_info_data.isin');
    //
    //             if (!result.hasOwnProperty(isin)) {
    //               result[isin] = {
    //                 ...component,
    //                 ids: [component.id]
    //               }
    //             } else {
    //               result[isin].ids.push(component.id)
    //             }
    //
    //             return result;
    //           }, []);
    //
    //           activeComponents = Object.values(activeComponents);
    //
    //           setAssets(activeComponents);
    //           setInactiveAssets(inactiveComponents);
    //           handleSelectedAssetsChange(selectedAssets.map(currentlySelected => {
    //             if (!currentlySelected.length || isAggregatedSelected) {
    //               return activeComponents;
    //             }
    //
    //             return _.intersectionBy(activeComponents, currentlySelected, (component) => component.id);
    //           }))
    //         }, (errors) => {
    //         })
    //       }).finally(() => setAssetsLoading(false))
    //   }
    // }, [selectedDates]);

    useEffect(() => {
      if (isMounted) {
        setSelectedRange(calculateSelectedRange())
      }
    }, [selectedDates, selectedPortfolios]);

    const fetchDataForCustomer = () => {
      if (customer.data && !customer.loading && !customer.errors) {
        fetchData().then(() => setDataLoading(false));
      }
    };

    const handleSelectedPortfoliosChange = (selectedPortfolios) => {
      setSelectedPortfolios(selectedPortfolios);

      if(selectedPortfolios){
        // deselect aggregated in case single depot
        if(selectedPortfolios.length === 1) {
          setChartsSettings(prevChartSettings => {
            const updatedChartsSettings = {...prevChartSettings};
            _.set(updatedChartsSettings, 'historical.aggregateSelected', false);
            _.set(updatedChartsSettings, 'performance.aggregateSelected', false);

            return updatedChartsSettings;
          });
        }

        const customerPortfolios = portfolios.data || [];
        const customerPortfoliosCount = customerPortfolios.length;
        let hasOnlyOnePortfolio = customerPortfoliosCount === 1;
        const depot_id = hasOnlyOnePortfolio ? [0] : PortfolioUtils.getSelectedPortfoliosNumbers(selectedPortfolios);

        let currentPeriodInvalid = false;
        if(dataFetchingOptions.current.start_date){
          let portfoliosToUse = hasOnlyOnePortfolio
            ? customerPortfolios  // if customer has only one portfolio - no aggregated in customerPortfolios => all customer portfolios are used
            : customerPortfolios.filter(portfolio => depot_id.includes(portfolio.depotNumber));
          let minStartOfInvestment = getPortfolioMinStartOfInvestment(portfoliosToUse); // get earliest start of investment among selected portfolios
          currentPeriodInvalid = minStartOfInvestment.isAfter(moment(dataFetchingOptions.current.start_date))
        }

        dataFetchingOptions.current.depot_id = depot_id;

        if(customerPortfoliosCount && currentPeriodInvalid){
          // date change will fetchDataForCustomer with new dates
          handleSelectedDatesChanged(PERFORMANCE_TIME_TYPE.BEGINNING.getDateRange(), 'BEGINNING')
        } else {
          fetchDataForCustomer();
        }
      }
    };

    const handleSelectedAssetsChange = (newAssets) => {
      if (newAssets && JSON.stringify(selectedAssets) !== JSON.stringify(newAssets)) {
        dataFetchingOptions.current.assets = assets.length === newAssets.length && assets.length > 1 ? [] : getSelectedAssetsIds(newAssets);
        fetchDataForCustomer();
      }
    };
    //#endregion

    const fetchData = async (updatedChartSettings, customerData) => {
      setDataLoading(true);
      return fetchPortfolioData(updatedChartSettings || chartsSettings, customerData || customer.data);
    };

    const allDataSourcesCallbacks = [
      setInvestmentData,
      setPortfolioData
    ];

    //#region Data fetching

    const fetchCustomer = async () => {
      startLoadTime.current = new Date().getTime();
      const id = getRequestedCustomerId();

      setCustomer({
        ...customer,
        loading: true
      });
      setPortfolios({
        ...portfolios,
        loading: true,
      });

      try {
        // withHistoricalPortfolios should be taken from settings in db - so undefined
        const customerAppData = await dataProvider.getCustomerAppData(id, true, false, false, false, false);
        /** Parse shared settings data */
        parseResponse(customerAppData, 'shared_settings', (data) => {
          props.dispatch(setSharedSettings(data || {}));
        }, () => props.dispatch(setSharedSettings({})))

        /** Parse general customer data, portfolios data */
        parseResponse(customerAppData, 'customer', (data) => {

          let portfoliosData = data && _.get(data, 'portfolios', [])

          let customerPortfolios = data && data.portfolios && data.portfolios.portfolios || [];
          const defaultExcludedPortfolios = convertSettingsToSelectedPortfolios(
            customerPortfolios, data.dashboard_settings, 'excluded_portfolios');
          customerPortfolios = filterOutPortfoliosToExclude(customerPortfolios, defaultExcludedPortfolios);

          let trackingDate;

          if(!_.isEmpty(portfoliosData)){
            trackingDate = portfoliosData.min_tracking_date;
          }

          // getting type of selected time frame
          let timeFrameCode = REPORT_DATE_PERFORMANCE_TIME_TYPE_MAPPING[data.dashboard_settings.date_range];

          // getting selected dates that are set by default when dashboard is loaded
          let defaultSelectedDates = PERFORMANCE_TIME_TYPE[timeFrameCode].getDateRange();

          if(timeFrameCode === CUSTOM){
            if (data.dashboard_settings.date_range_start_date && data.dashboard_settings.date_range_end_date) {
              defaultSelectedDates.start = moment(data.dashboard_settings.date_range_start_date);
              defaultSelectedDates.end = moment(data.dashboard_settings.date_range_end_date);
            }
          }

          let start_date = defaultSelectedDates.start && defaultSelectedDates.start.format('YYYY-MM-DD') || undefined;
          // if start date of time period is older than portfolios, set time period to beginning
          if (trackingDate > start_date) {
            timeFrameCode = BEGINNING;
            defaultSelectedDates = PERFORMANCE_TIME_TYPE[timeFrameCode].getDateRange();
          }

          // dates and period that are selected in time selector
          setSelectedPeriodData({selectedDates: defaultSelectedDates, selectedDatesType: timeFrameCode});

          dataFetchingOptions.current = {
            ...dataFetchingOptions.current,
            start_date: defaultSelectedDates.start && defaultSelectedDates.start.format('YYYY-MM-DD') || undefined,
            end_date: defaultSelectedDates.end && defaultSelectedDates.end.format('YYYY-MM-DD') || undefined,
            excluded_depot_id: defaultExcludedPortfolios ? PortfolioUtils.getSelectedPortfoliosNumbers(defaultExcludedPortfolios) : undefined
          };

          setSelectedPortfolios(convertSettingsToSelectedPortfolios(customerPortfolios, data.dashboard_settings));

          setCustomer({
            data: data,
            loading: false,
            errors: undefined
          });
          setPortfolios({
            data: [...customerPortfolios],
            loading: false,
            errors: undefined
          });

          setSelectedRange(calculateSelectedRange(selectedPortfolios ? [...selectedPortfolios] : [...customerPortfolios]))

          initCustomerData(data);

        }, (error) => {

          setCustomer({
            data: undefined,
            loading: false,
            errors: error
          });

          toggleLoadingStatus(allDataSourcesCallbacks);

        })

      }
      catch(errors) {
        if (errors.status === 403) {
          handleUnauthorized();
        } else {
          setCustomer({
            data: undefined,
            loading: false,
            errors
          });

          toggleLoadingStatus(allDataSourcesCallbacks);
        }
      }
    };

    const fetchPortfolioData = async (chartsSettings, customerData) => {
      const id = getRequestedCustomerId();

      setInstrumentsGroupBy(GROUP_ACTION_DEPOT);
      setPortfolioData({
        data: undefined,
        loading: true,
        errors: undefined,
        updatedAt: +new Date()
      });

      try {

        let {
          depot_id,
          excluded_depot_id,
          start_date,
          end_date,
          assets
        } = dataFetchingOptions.current;

        const withOtherAssets = _.get(customerData, 'dashboard_settings.with_other_assets', true);
        const withLiabilities = getSubSystemConfigItem('reporting', 'useNewOtherAssets') && _.get(customerData, 'dashboard_settings.with_liabilities', false);
        let response = await dataProvider.getPortfolioData(id, depot_id, start_date, end_date,
          chartsSettings.global.withHistoricalPortfolios, assets, undefined, excluded_depot_id, withOtherAssets,
            getCancelToken(), withLiabilities);

        const data = {};
        let responseDataKeys = ['payment_plans', 'payout_plans', 'switch_plans', 'breakdown'];

        responseDataKeys.forEach(key => {
          parseResponse(response, key, (_data) => {
            data[key] = {
              data: _data,
              errors: undefined
            }
          }, (errors) => {
            data[key] = {
              data: undefined,
              errors: getErrorMessage(errors)
            }
          });
        });

        /** Parse financial data (header 2) */
        parseResponse(response, 'financial', (data) => {
          if (data.data.start_date) {
            let startInvestment = moment(data.data.start_date);
            if (startInvestment > selectedDates.start) {
              setSelectedPeriodData({
                selectedDates: DEFAULT_PERFORMANCE_TIME_TYPE.getDateRange(),
                selectedDatesType: DEFAULT_PERFORMANCE_TIME_TYPE
              });
              // TODO: check if we need to handlePeriodChanged()
            }
          }
          setInvestmentData({
            data: data && data.data,
            loading: false,
            loadingPerformanceOnly: false,
            errors: undefined
          });
        }, (errors) => {
          setInvestmentData({
            data: undefined,
            loading: false,
            loadingPerformanceOnly: false,
            errors: showErrors(errors)
          });
        });

        responseDataKeys = [
          ['profit_loss', []],
          ['instruments', []],
          ['profit_loss_summary', {}],
          ['by_ms_category', []],
          ['by_products', []],
          ['by_risk_indicators', []]
        ];

        parseResponse(response, 'instruments_table', (_data) => {
          responseDataKeys.forEach(([key, defaultValue]) => {
            data[key] = {
              data: _data[key] || defaultValue,
              errors: undefined
            }
          })
        }, (errors) => {
          responseDataKeys.forEach(([key, ]) => {
            data[key] = {
              data: undefined,
              errors: getErrorMessage(errors)
            }
          })
        });

        setPortfolioData({
          data: data,
          loading: false,
          errors: undefined,
          updatedAt: +new Date()
        });

      } catch (errors) {
        if(isCancel(errors)) return;

        if (errors.status === 403) {
          handleUnauthorized();
        }
        setPortfolioData({
          data: undefined,
          loading: true,
          errors: errors,
          updatedAt: +new Date()
        });
      }
    };

    //#endregion
    const handleUnauthorized = () => {
      if (!isCurrentCustomerData) {
        brokerLogout();
      } else {
        customerLogout();
      }
    }

    const refresh = () => {
      setMountingStatus(false);
      fetchCustomer();
    };

    const handleSelectedDatesChanged = (dates, datesType) => {
      const newPeriodData = {selectedDates: dates, selectedDatesType: datesType};
      setSelectedPeriodData(newPeriodData);

      dataFetchingOptions.current = {
        ...dataFetchingOptions.current,
        start_date: dates.start && dates.start.format('YYYY-MM-DD') || undefined,
        end_date: dates.end && dates.end.format('YYYY-MM-DD') || undefined,
      };

      fetchDataForCustomer();
    };

    // scroll top on refresh
    React.useEffect(() => {
      document.getElementById("app-main").scrollTo(0,0)
    }, [dataLoading]);

    const groupedPortfolios = _.keyBy((portfolios.data || []), 'depotNumber');

    return (
      <WrappedComponent
        customer={customer}
        portfolios={portfolios}
        investmentData={investmentData}
        instrumentList={getPortfolioDataByKey(instrumentsGroupBy, portfolioData, portfolios, groupedPortfolios)}
        profitAndLoss={getPortfolioDataByKey('profit_loss', portfolioData, portfolios, groupedPortfolios)}
        profitAndLossSummary={getPortfolioDataByKey('profit_loss_summary', portfolioData, portfolios, groupedPortfolios)}
        paymentPlans={getPortfolioDataByKey('payment_plans', portfolioData, portfolios, groupedPortfolios)}
        payoutPlansData={getPortfolioDataByKey('payout_plans', portfolioData, portfolios, groupedPortfolios)}
        switchPlansData={getPortfolioDataByKey('switch_plans', portfolioData, portfolios, groupedPortfolios)}
        selectedDates={selectedDates}
        selectedDatesType={selectedDatesType}
        onSelectedDatesChanged={handleSelectedDatesChanged}
        selectedPortfolios={selectedPortfolios}
        excludedPortfolios={dataFetchingOptions.current.excluded_depot_id}
        breakdownData={getPortfolioDataByKey('breakdown', portfolioData, portfolios, groupedPortfolios)}
        assets={assets}
        inactiveAssets={inactiveAssets}
        selectedAssets={selectedAssets}
        isAllAssetsSelected={assets.length > 1 && assets.length === selectedAssets.length}
        onSelectedAssetsChanged={handleSelectedAssetsChange}
        onSelectedPortfoliosChanged={handleSelectedPortfoliosChange}
        isVirtual={isVirtual}
        refreshData={fetchDataForCustomer}
        refresh={refresh}
        selectedRange={selectedRange}
        includeHistoricalPortfolios={chartsSettings.global.withHistoricalPortfolios} // todo refactor, there is no sense passing this prop as we already pass chartsSettings
        chartsSettings={chartsSettings}
        handleChartSettingsChange={handleChartSettingsChange}
        reportType={reportType}
        setReportType={setReportType}
        instrumentsGroupBy={instrumentsGroupBy}
        handleInstrumentsGroupByChange={setInstrumentsGroupBy}
        isMounted={isMounted}
        dataProvider={dataProvider}
        {...props}
      />
    )
  })
};

export default withDashboardData;